import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BarChart3, Check, Database, Hexagon, LineChart, Server } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { billingService } from '../../services/billing';
import styles from './PricingPage.module.css';

const ANALYTICS_TIERS = [
  { key: 'beta', name: 'Beta', monthly: 10000 },
  { key: 'alpha', name: 'Alpha', monthly: 25000 },
  { key: 'alphaplus', name: 'Alpha+', monthly: 50000 },
];

const ERP_MONTHLY = 1;

const INDUSTRIES = ['Education', 'Healthcare', 'Retail', 'Other'];

const isIndianMobile = (value) => {
  const compact = String(value || '').replace(/[\s()-]/g, '');
  if (/^[6-9]\d{9}$/.test(compact)) return true;
  const stripped = compact.replace(/^\+91/, '').replace(/^91(?=\d{10}$)/, '').replace(/^0/, '');
  return /^[6-9]\d{9}$/.test(stripped);
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_PATTERN = /^[\p{L}\s.'-]+$/u;

const quoteFieldErrors = (quote) => {
  const errors = {};
  const organization = quote.organization.trim();
  const contactName = quote.contactName.trim();
  const email = quote.email.trim();

  if (organization.length < 2) {
    errors.organization = 'Enter your organization name (at least 2 characters).';
  } else if (organization.length > 120) {
    errors.organization = 'Organization name must be 120 characters or fewer.';
  }

  if (!INDUSTRIES.includes(quote.industry)) {
    errors.industry = 'Select an industry.';
  }

  if (contactName.length < 2) {
    errors.contactName = 'Enter a contact name (at least 2 characters).';
  } else if (!NAME_PATTERN.test(contactName)) {
    errors.contactName = 'Contact name can only contain letters, spaces, hyphens, and apostrophes.';
  }

  if (!email) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.email = 'Enter a valid email address.';
  }

  if (!quote.phone.trim()) {
    errors.phone = 'Phone number is required.';
  } else if (!isIndianMobile(quote.phone)) {
    errors.phone = 'Enter a valid 10-digit Indian mobile number.';
  }

  return errors;
};

const EMPTY_QUOTE = {
  organization: '',
  industry: INDUSTRIES[0],
  contactName: '',
  email: '',
  phone: '',
};

const formatINR = (value) => `₹${value.toLocaleString('en-IN')}`;

const PricingPage = () => {
  const navigate = useNavigate();
  const { refreshUser, logout } = useAuth();
  const { showInfo, showError, showSuccess } = useToast();
  const [paymentsEnabled, setPaymentsEnabled] = useState(null);

  useEffect(() => {
    let mounted = true;
    billingService
      .getConfig()
      .then((config) => mounted && setPaymentsEnabled(Boolean(config?.enabled)))
      .catch(() => mounted && setPaymentsEnabled(false));
    return () => {
      mounted = false;
    };
  }, []);

  const [selected, setSelected] = useState(() => new Set(['erp']));
  const [analyticsTier, setAnalyticsTier] = useState('beta');
  const [processing, setProcessing] = useState(false);

  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quote, setQuote] = useState(EMPTY_QUOTE);
  const [quoteErrors, setQuoteErrors] = useState({});
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteRequested, setQuoteRequested] = useState(false);

  const toggleProduct = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onCardKeyDown = (event, handler) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handler();
    }
  };

  const tier = ANALYTICS_TIERS.find((t) => t.key === analyticsTier) || ANALYTICS_TIERS[0];

  const hasAnalytics = selected.has('analytics');

  const monthlyTotal = useMemo(() => {
    let total = 0;
    if (hasAnalytics) total += tier.monthly;
    if (selected.has('erp')) total += ERP_MONTHLY;
    return total;
  }, [selected, hasAnalytics, tier]);

  const hasBenchmark = selected.has('benchmark');

  const openQuote = (event) => {
    event.stopPropagation();
    setQuoteErrors({});
    setQuoteOpen(true);
  };

  const updateQuote = (field) => (event) => {
    const value = event.target.value;
    setQuote((prev) => ({ ...prev, [field]: value }));
    setQuoteErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const submitQuote = async () => {
    const errors = quoteFieldErrors(quote);
    setQuoteErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const organization = quote.organization.trim();
    const contactName = quote.contactName.trim();
    const email = quote.email.trim();
    const phone = quote.phone.trim();
    setQuoteSending(true);
    try {
      await billingService.createQuoteRequest({
        organization,
        industry: quote.industry,
        contactName,
        email,
        phone,
      });
      setQuoteOpen(false);
      setQuoteRequested(true);
      setSelected((prev) => new Set(prev).add('enterprise'));
      showSuccess("Quote requested. We'll follow up shortly.");
    } catch (error) {
      showError(error.message || 'Could not send your request. Please try again.');
    } finally {
      setQuoteSending(false);
    }
  };

  const handleContinue = async () => {
    if (paymentsEnabled === false) {
      showInfo('Payments are not enabled yet. Taking you to your dashboard.', 3000);
      navigate('/dashboard', { replace: true });
      return;
    }

    if (!selected.has('analytics') && !selected.has('erp')) {
      showError('Benchmark IQ is billed per metric. Add Analytics or ERP to pay now.');
      return;
    }

    setProcessing(true);
    try {
      const result = await billingService.checkout({
        analyticsTier,
        products: Array.from(selected),
      });
      if (result.status === 'dismissed') {
        showInfo('Payment was cancelled. You can pay any time from Profile → Billing.', 4000);
        return;
      }
      await refreshUser();
      showSuccess('Payment successful! Your plan is active.');
      navigate('/profile?tab=billing', { replace: true });
    } catch (error) {
      showError(error.message || 'Payment could not be completed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const continueLabel =
    paymentsEnabled === false
      ? 'Continue to dashboard'
      : monthlyTotal > 0
        ? `Pay ${formatINR(monthlyTotal)}`
        : 'Select Analytics or ERP';

  const cardClass = (isSelected, locked) =>
    `${styles.card} ${isSelected ? styles.selected : ''} ${locked ? styles.locked : ''}`;

  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <div className={styles.brand}>
          <Hexagon size={19} className={styles.brandIcon} />
          <span>AnalyticaX</span>
        </div>
        <div className={styles.topbarActions}>
          <button type="button" className={styles.btn} onClick={handleLogout}>
            Logout
          </button>
          <a href="/" className={styles.btn}>
            Back to homepage
          </a>
        </div>
      </div>

      <div className={styles.hero}>
        <span className={styles.eyebrow}>before we get started</span>
        <h1 className={styles.title}>Choose what you'd like to set up</h1>
        <p className={styles.subtitle}>
          Pick Analytics, ERP, Benchmark IQ, or any combination. Analytics is optional.
        </p>
      </div>

      <div className={styles.grid}>
        {/* ERP */}
        <div
          className={cardClass(selected.has('erp'), false)}
          role="button"
          tabIndex={0}
          aria-pressed={selected.has('erp')}
          onClick={() => toggleProduct('erp')}
          onKeyDown={(e) => onCardKeyDown(e, () => toggleProduct('erp'))}
        >
          <span className={styles.check}>
            <Check size={12} />
          </span>
          <Database size={22} className={styles.cardIcon} />
          <h3 className={styles.cardTitle}>ERP</h3>
          <p className={styles.cardText}>
            Digitize operations and onboard data — build your own pages and master tables.
          </p>
          <span className={styles.price}>{formatINR(ERP_MONTHLY)} / month</span>
        </div>

        <div
          className={cardClass(hasAnalytics, false)}
          role="button"
          tabIndex={0}
          aria-pressed={hasAnalytics}
          onClick={() => toggleProduct('analytics')}
          onKeyDown={(e) => onCardKeyDown(e, () => toggleProduct('analytics'))}
        >
          <span className={styles.check}>
            <Check size={12} />
          </span>
          <BarChart3 size={22} className={styles.cardIcon} />
          <h3 className={styles.cardTitle}>Analytics</h3>
          <p className={styles.cardText}>
            Custom templates, normalized schema, embedded Power BI visuals.
          </p>
          <div className={styles.tierSelect} onClick={(e) => e.stopPropagation()}>
            {ANALYTICS_TIERS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`${styles.tierOpt} ${
                  hasAnalytics && analyticsTier === option.key ? styles.tierSelected : ''
                }`}
                aria-pressed={hasAnalytics && analyticsTier === option.key}
                onClick={() => {
                  setAnalyticsTier(option.key);
                  setSelected((prev) => new Set(prev).add('analytics'));
                }}
              >
                <span>{option.name}</span>
                <span className={styles.tierPrice}>{formatINR(option.monthly)}/mo</span>
              </button>
            ))}
          </div>
        </div>

        {/* Benchmark IQ */}
        <div
          className={cardClass(hasBenchmark, false)}
          role="button"
          tabIndex={0}
          aria-pressed={hasBenchmark}
          onClick={() => toggleProduct('benchmark')}
          onKeyDown={(e) => onCardKeyDown(e, () => toggleProduct('benchmark'))}
        >
          <span className={styles.check}>
            <Check size={12} />
          </span>
          <LineChart size={22} className={styles.cardIcon} />
          <h3 className={styles.cardTitle}>Benchmark IQ</h3>
          <p className={styles.cardText}>
            See where you stand against peers by size and location.
          </p>
          <span className={styles.price}>{formatINR(1000)} per metric</span>
        </div>

        {/* Enterprise (quote only) */}
        <div className={cardClass(quoteRequested, true)}>
          <span className={styles.check}>
            <Check size={12} />
          </span>
          <Server size={22} className={styles.cardIcon} />
          <h3 className={styles.cardTitle}>Enterprise</h3>
          <p className={styles.cardText}>
            Native or managed Fabric/Power BI deployment in your own environment.
          </p>
          <button
            type="button"
            className={`${styles.btn} ${styles.quoteButton}`}
            onClick={openQuote}
            disabled={quoteRequested}
          >
            {quoteRequested ? 'Quote requested' : 'Ask for Quote'}
          </button>
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.summary}>
          <span>
            {selected.size} product{selected.size === 1 ? '' : 's'} selected
          </span>
          <span className={styles.total}>
            {formatINR(monthlyTotal)} / mo{hasBenchmark ? ' + per metric' : ''}
          </span>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.payButton}
            onClick={handleContinue}
            disabled={processing || paymentsEnabled === null || (paymentsEnabled && monthlyTotal <= 0)}
          >
            {processing ? 'Processing...' : continueLabel}
            {!processing && <ArrowRight size={14} />}
          </button>
        </div>
      </div>
      {hasBenchmark && (
        <p className={styles.note}>
          Benchmark IQ is billed per metric and isn't charged at checkout. We'll set it up with you
          after you choose your metrics.
        </p>
      )}

      {quoteOpen && (
        <div className={styles.overlay} onClick={() => !quoteSending && setQuoteOpen(false)}>
          <div
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="quote-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="quote-title" className={styles.dialogTitle}>
              Request an Enterprise quote
            </h3>
            <p className={styles.dialogSub}>
              Tell us a bit about your organization and we'll follow up with pricing.
            </p>

            <label htmlFor="quote-org">Organization name</label>
            <input
              id="quote-org"
              type="text"
              value={quote.organization}
              onChange={updateQuote('organization')}
              placeholder="e.g. Greenwood International School"
              maxLength={120}
              aria-invalid={Boolean(quoteErrors.organization)}
            />
            {quoteErrors.organization && <p className={styles.fieldError}>{quoteErrors.organization}</p>}

            <label htmlFor="quote-industry">Industry</label>
            <select id="quote-industry" value={quote.industry} onChange={updateQuote('industry')}>
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
            {quoteErrors.industry && <p className={styles.fieldError}>{quoteErrors.industry}</p>}

            <label htmlFor="quote-contact">Contact name</label>
            <input
              id="quote-contact"
              type="text"
              value={quote.contactName}
              onChange={updateQuote('contactName')}
              placeholder="Your name"
              aria-invalid={Boolean(quoteErrors.contactName)}
            />
            {quoteErrors.contactName && <p className={styles.fieldError}>{quoteErrors.contactName}</p>}

            <label htmlFor="quote-email">Email</label>
            <input
              id="quote-email"
              type="email"
              value={quote.email}
              onChange={updateQuote('email')}
              placeholder="you@organization.com"
              autoComplete="email"
              aria-invalid={Boolean(quoteErrors.email)}
            />
            {quoteErrors.email && <p className={styles.fieldError}>{quoteErrors.email}</p>}

            <label htmlFor="quote-phone">Phone number</label>
            <input
              id="quote-phone"
              type="tel"
              inputMode="tel"
              value={quote.phone}
              onChange={updateQuote('phone')}
              placeholder="+91 98765 43210"
              autoComplete="tel"
              aria-invalid={Boolean(quoteErrors.phone)}
            />
            {quoteErrors.phone && <p className={styles.fieldError}>{quoteErrors.phone}</p>}

            <div className={styles.dialogFooter}>
              <button
                type="button"
                className={styles.btn}
                onClick={() => setQuoteOpen(false)}
                disabled={quoteSending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnAccent}`}
                onClick={submitQuote}
                disabled={quoteSending}
              >
                {quoteSending ? 'Sending...' : 'Submit request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
